// Factory utilities for creating edge template objects and converting properties between template and API formats.

import FontUtils from '@/services/canvas/fonts/Fonts';

const { fontFamilyGetNameByUrl } = FontUtils();

interface FontEntry {
	id: number;
	family_name: string;
	[key: string]: unknown;
}

interface EdgeTemplateData {
	id: number | string;
	name: string;
	type: string;
	line_start: string | null;
	line_start_size: number | null;
	line_end: string | null;
	line_end_size: number | null;
	border_color: string | null;
	border_style: string | null;
	border_width: number | null;
	shadow_enabled: boolean | null;
	shadow_x: number | null;
	shadow_y: number | null;
	shadow_blur: number | null;
	shadow_color: string | null;
	font_color: string | null;
	font_size: number | null;
	font_url: string | null;
	font_weight: string | null;
	font_style: string | null;
	text_position: string | null;
	text_decoration: string | null;
}

interface EdgeTemplateObject {
	id: number | string;
	name: string;
	type: string;
	lineStart: string | null;
	lineStartSize: number | null;
	lineEnd: string | null;
	lineEndSize: number | null;
	border: true;
	borderColor: string | null;
	borderStyle: string | null;
	borderWidth: number | null;
	shadow: boolean | null;
	shadowX: number | null;
	shadowY: number | null;
	shadowBlur: number | null;
	shadowColor: string | null;
	fontColor: string | null;
	fontSize: number | null;
	fontFamily: string | null;
	fontWeight: string | null;
	fontStyle: string | null;
	textPosition: string | null;
	textDecoration: string | null;
	contentStart: string;
	contentMiddle: string;
	contentEnd: string;
}

const EDGE_TEMPLATE_MAPPING: Record<string, string> = {
	border: 'border_enabled',
	borderColor: 'border_color',
	borderStyle: 'border_style',
	borderWidth: 'border_width',
	fontColor: 'font_color',
	fontSize: 'font_size',
	fontWeight: 'font_weight',
	fontStyle: 'font_style',
	fontFamily: 'font_url',
	lineHeight: 'line_height',
	shadowBlur: 'shadow_blur',
	shadowColor: 'shadow_color',
	shadow: 'shadow_enabled',
	shadowX: 'shadow_x',
	shadowY: 'shadow_y',
	textDecoration: 'text_decoration',
	textPosition: 'text_position',
	textAlign: 'text_align',
	lineStart: 'line_start',
	lineStartSize: 'line_start_size',
	lineEnd: 'line_end',
	lineEndSize: 'line_end_size',
};

function mappingInverted(mapping: Record<string, string>): Record<string, string> {
	const invertedMapping: Record<string, string> = {};
	for (const [key, value] of Object.entries(mapping)) {
		invertedMapping[value] = key;
	}
	return invertedMapping;
}

function createEdgeTemplateObject(
	templateData: EdgeTemplateData,
	fonts: Record<string, FontEntry>,
): EdgeTemplateObject {
	return {
		id: templateData.id,
		name: templateData.name,
		type: templateData.type,
		lineStart: templateData.line_start,
		lineStartSize: templateData.line_start_size,
		lineEnd: templateData.line_end,
		lineEndSize: templateData.line_end_size,
		border: true,
		borderColor: templateData.border_color,
		borderStyle: templateData.border_style,
		borderWidth: templateData.border_width,
		shadow: templateData.shadow_enabled,
		shadowX: templateData.shadow_x,
		shadowY: templateData.shadow_y,
		shadowBlur: templateData.shadow_blur,
		shadowColor: templateData.shadow_color,
		fontColor: templateData.font_color,
		fontSize: templateData.font_size,
		fontFamily: fontFamilyGetNameByUrl(templateData.font_url, fonts),
		fontWeight: templateData.font_weight,
		fontStyle: templateData.font_style,
		textPosition: templateData.text_position,
		textDecoration: templateData.text_decoration,
		contentStart: 'START',
		contentMiddle: 'Label',
		contentEnd: 'END',
	};
}

function convertOriginalProperty(
	originalProperty: Record<string, unknown>,
): Record<string, unknown> | undefined {
	const [[key, value]] = Object.entries(originalProperty);
	const mapping = mappingInverted(EDGE_TEMPLATE_MAPPING);
	if (mapping[key]) return { [mapping[key]]: value };
	return undefined;
}

function convertToOriginalProperty(
	templateProperty: Record<string, unknown>,
): Record<string, unknown> | undefined {
	const [[key, value]] = Object.entries(templateProperty);
	if (EDGE_TEMPLATE_MAPPING[key]) return { [EDGE_TEMPLATE_MAPPING[key]]: value };
	return undefined;
}

export { createEdgeTemplateObject, convertOriginalProperty, convertToOriginalProperty };

export type { EdgeTemplateData, EdgeTemplateObject, FontEntry };
